import { useContext, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Empty,
  Input,
  Modal as AntModal,
  Tooltip,
  message,
} from "antd";
import {
  CloseOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  PushpinFilled,
  PushpinOutlined,
  SearchOutlined,
} from "@ant-design/icons";

import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";
import ViewsPageSelector from "./ViewsPageSelector";
import AppContext from "../../../../context/AppContext";
import { SIDER_TABS } from "../../../../utils/Constants";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import fallbackCollectionIcon from "../../../../assets/icons/ic_casino.svg";
import {
  DEFAULT_PAGE_CODE,
  useAddAdminViewPlacements,
  useAdminPages,
  useAdminViewPlacements,
  useAdminViews,
  useDeleteAdminViewPlacement,
  useMoveAdminViewPlacement,
  usePatchAdminViewPlacement,
  VIEW_PLACEMENT_PIN_TO_TOP,
  type AdminViewPlacement,
  type ViewKey,
} from "../../../../queries/casino-configuration/views/useAdminViews";
import { usePamFeature } from "../../../../hooks/useFeatureFlag";
import {
  useCurationDisplayName,
  type CurationDisplayName,
} from "../../../../hooks/useCurationDisplayName";
import CurationEntityName from "../CurationEntityName";
import {
  useFlatAdminCollections,
  type AdminCollection,
} from "../../../../queries/casino-configuration/collections/useAdminCollections";
import { formatString } from "../../../../utils/constants/stringUtils";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";

import "./stylesViewsConfiguration.css";

const SEARCH_DEBOUNCE_MS = 250;

const VIEW_LABELS: Record<string, string> = {
  carousels: AppStrings.CASINO_CONFIG_VIEWS_TAB_CAROUSELS,
  shortcuts: AppStrings.CASINO_CONFIG_VIEWS_TAB_SHORTCUTS,
};

const VIEW_TAB_INFO: Record<string, string> = {
  carousels: AppStrings.CASINO_CONFIG_VIEWS_TAB_INFO_CAROUSELS,
  shortcuts: AppStrings.CASINO_CONFIG_VIEWS_TAB_INFO_SHORTCUTS,
};

function labelForView(view: string): string {
  return (
    VIEW_LABELS[view] ??
    view.charAt(0).toUpperCase() + view.slice(1).replace(/_/g, " ")
  );
}

type PlacementRowProps = {
  placement: AdminViewPlacement;
  index: number;
  dragOverIdx: number | null;
  onDragStart: (idx: number) => void;
  onDragOver: (e: React.DragEvent, idx: number) => void;
  onDrop: (dropIdx: number) => void;
  onDragCancel: () => void;
  onTogglePin: (placement: AdminViewPlacement) => void;
  onToggleActive: (placement: AdminViewPlacement) => void;
  onRequestRemove: (placement: AdminViewPlacement) => void;
  /** Flag- and locale-aware display name; resolved once at the page level. */
  getName: CurationDisplayName["getName"];
};

function PlacementRow({
  placement,
  index,
  dragOverIdx,
  onDragStart,
  onDragOver,
  onDrop,
  onDragCancel,
  onTogglePin,
  onToggleActive,
  onRequestRemove,
  getName,
}: PlacementRowProps) {
  const { collection, is_active: isActive, is_pinned: isPinned } = placement;
  const displayName = getName(collection);
  const memberCount = collection.member_count ?? 0;
  const childCount = collection.child_count ?? 0;
  const memberLabel =
    collection.kind === "providers"
      ? AppStrings.CASINO_CONFIG_VIEWS_PROVIDERS_SUFFIX
      : AppStrings.CASINO_CONFIG_VIEWS_GAMES_SUFFIX;
  const collectionLabel =
    collection.kind === "providers"
      ? AppStrings.CASINO_CONFIG_VIEWS_PROVIDERS_COLLECTION
      : AppStrings.CASINO_CONFIG_VIEWS_GAMES_COLLECTION;

  return (
    <div
      className={`views-row ${isActive ? "" : "views-row--hidden"} ${
        dragOverIdx === index ? "views-row--drop-target" : ""
      }`}
      data-testid={`view-placement-row-${collection.code}`}
      draggable
      onDragStart={() => onDragStart(index)}
      onDragOver={(e) => onDragOver(e, index)}
      onDrop={() => onDrop(index)}
      onDragEnd={() => onDragCancel()}
    >
      <Tooltip title={AppStrings.CASINO_CONFIG_VIEWS_ROW_DRAG_TOOLTIP}>
        <span
          className="views-row-drag"
          aria-label={AppStrings.CASINO_CONFIG_VIEWS_ROW_DRAG_LABEL}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
        >
          <HolderOutlined />
        </span>
      </Tooltip>
      <span className="views-row-index">{index + 1}</span>
      <img
        className="views-row-icon"
        src={collection.icon_url || fallbackCollectionIcon}
        alt=""
      />
      <div className="views-row-text">
        <div className="views-row-title">
          <CurationEntityName
            className="views-row-name"
            entity={collection}
            displayName={displayName}
          />
          <span className="views-row-code">{collection.code}</span>
          <span className="views-row-kind">{collectionLabel}</span>
          {isPinned && (
            <span className="views-row-pinned-badge">
              {AppStrings.CASINO_CONFIG_VIEWS_PIN_BADGE}
            </span>
          )}
        </div>
        <div className="views-row-meta">
          <span>
            {childCount}{" "}
            {AppStrings.CASINO_CONFIG_VIEWS_SUB_COLLECTIONS_SUFFIX}
          </span>
          <span>
            {memberCount} {memberLabel}
          </span>
        </div>
      </div>
      <Tooltip
        title={
          isPinned
            ? AppStrings.CASINO_CONFIG_VIEWS_ROW_UNPIN_TOOLTIP
            : AppStrings.CASINO_CONFIG_VIEWS_ROW_PIN_TOOLTIP
        }
      >
        <button
          type="button"
          className={`views-row-pin ${
            isPinned ? "views-row-pin--active" : ""
          }`}
          aria-label={
            isPinned
              ? AppStrings.CASINO_CONFIG_VIEWS_ROW_UNPIN
              : AppStrings.CASINO_CONFIG_VIEWS_ROW_PIN
          }
          onClick={() => onTogglePin(placement)}
        >
          {isPinned ? <PushpinFilled /> : <PushpinOutlined />}
        </button>
      </Tooltip>
      <span
        className={`views-row-status ${
          isActive ? "views-row-status--shown" : "views-row-status--hidden"
        }`}
      >
        {isActive
          ? AppStrings.CASINO_CONFIG_VIEWS_STATUS_SHOWN
          : AppStrings.CASINO_CONFIG_VIEWS_STATUS_HIDDEN}
      </span>
      <Tooltip title={AppStrings.CASINO_CONFIG_VIEWS_ROW_TOGGLE_TOOLTIP}>
        <label className="views-row-toggle">
          <input
            type="checkbox"
            checked={isActive}
            onChange={() => onToggleActive(placement)}
            aria-label={`${
              isActive
                ? AppStrings.CASINO_CONFIG_VIEWS_ROW_TOGGLE_HIDE_PREFIX
                : AppStrings.CASINO_CONFIG_VIEWS_ROW_TOGGLE_SHOW_PREFIX
            } ${displayName}`}
          />
          <span className="views-row-toggle-slider" />
        </label>
      </Tooltip>
      <Tooltip title={AppStrings.CASINO_CONFIG_VIEWS_ROW_REMOVE_TOOLTIP}>
        <button
          type="button"
          className="views-row-remove"
          aria-label={`${AppStrings.CASINO_CONFIG_VIEWS_ROW_REMOVE_PREFIX} ${displayName}`}
          onClick={() => onRequestRemove(placement)}
        >
          <CloseOutlined />
        </button>
      </Tooltip>
    </div>
  );
}

type AddViewPlacementModalProps = {
  open: boolean;
  view: ViewKey | null;
  pageCode: string;
  existingCodes: Set<string>;
  onClose: () => void;
  getName: CurationDisplayName["getName"];
};

function AddViewPlacementModal({
  open,
  view,
  pageCode,
  existingCodes,
  onClose,
  getName,
}: AddViewPlacementModalProps) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    const timer = window.setTimeout(
      () => setDebouncedSearch(search.trim()),
      SEARCH_DEBOUNCE_MS,
    );
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setSearch("");
      setDebouncedSearch("");
    }
  }, [open]);

  const { data, isLoading } = useFlatAdminCollections(
    debouncedSearch || undefined,
    open,
  );

  const { mutateAsync: addPlacements, isPending: adding } =
    useAddAdminViewPlacements(view ?? "", pageCode);

  const candidates: AdminCollection[] = useMemo(() => {
    const items = data?.items ?? [];
    return items.filter((c) => !existingCodes.has(c.code));
  }, [data, existingCodes]);

  const pathMap = useMemo(() => {
    const items = data?.items ?? [];
    const collectionByCode = new Map(items.map((c) => [c.code, c]));
    const result = new Map<string, string>();
    for (const c of items) {
      if (!c.parent_code) continue;
      const parts: string[] = [];
      let cursor: string | null | undefined = c.parent_code;
      while (cursor) {
        const parent = collectionByCode.get(cursor);
        if (!parent) break;
        parts.unshift(getName(parent));
        cursor = parent.parent_code;
      }
      if (parts.length > 0) result.set(c.code, parts.join(" › ") + " ›");
    }
    return result;
  }, [data, getName]);

  const toggleSelect = (code: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleAdd = async () => {
    const codes = Array.from(selected);
    if (!view || codes.length === 0) return;
    try {
      await addPlacements(codes);
      message.success(
        codes.length === 1
          ? AppStrings.CASINO_CONFIG_VIEWS_ADD_SUCCESS_SINGULAR
          : formatString(
              AppStrings.CASINO_CONFIG_VIEWS_ADD_SUCCESS_PLURAL,
              { count: String(codes.length) },
            ),
      );
      onClose();
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message || AppStrings.CASINO_CONFIG_VIEWS_ADD_FAILED,
        ),
      );
    }
  };

  return (
    <AntModal
      open={open}
      onCancel={onClose}
      footer={null}
      width={560}
      className="views-add-modal"
      destroyOnHidden
      closeIcon={<CloseOutlined />}
      title={
        <div>
          <div className="views-add-title">
            {AppStrings.CASINO_CONFIG_VIEWS_ADD_TITLE_PREFIX}{" "}
            {labelForView(view ?? "")}
          </div>
          <div className="views-add-selected">
            {formatString(AppStrings.CASINO_CONFIG_VIEWS_ADD_SELECTED, {
              count: String(selected.size),
            })}
          </div>
        </div>
      }
    >
      <Input
        className="views-add-search"
        prefix={<SearchOutlined />}
        placeholder={AppStrings.CASINO_CONFIG_VIEWS_ADD_SEARCH}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />
      <div className="views-add-list">
        {isLoading ? (
          <div className="views-loader">
            <RebetLoader height="48px" width="48px" />
          </div>
        ) : candidates.length === 0 ? (
          <Empty
            description={AppStrings.CASINO_CONFIG_VIEWS_ADD_NO_RESULTS}
          />
        ) : (
          candidates.map((item) => {
            const checked = selected.has(item.code);
            const kindLabel =
              item.kind === "providers"
                ? AppStrings.CASINO_CONFIG_VIEWS_PROVIDERS_COLLECTION
                : AppStrings.CASINO_CONFIG_VIEWS_GAMES_COLLECTION;
            const breadcrumb = pathMap.get(item.code);
            return (
              <div
                key={item.code}
                className={`views-add-row ${
                  checked ? "views-add-row--selected" : ""
                }`}
                onClick={() => toggleSelect(item.code)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    toggleSelect(item.code);
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <Checkbox
                  checked={checked}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSelect(item.code)}
                  className="views-add-row-checkbox"
                  aria-label={formatString(
                    AppStrings.CASINO_CONFIG_VIEWS_TOGGLE_ROW_ARIA,
                    { name: getName(item) },
                  )}
                />
                <div className="views-add-row-info">
                  {breadcrumb && (
                    <span className="views-add-row-path">{breadcrumb}</span>
                  )}
                  <CurationEntityName
                    className="views-add-row-name"
                    entity={item}
                    displayName={getName(item)}
                  />
                  <span className="views-add-row-code">{item.code}</span>
                  <span className="views-add-row-kind">{kindLabel}</span>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="views-add-footer">
        <button
          type="button"
          className="views-add-cancel"
          onClick={onClose}
          disabled={adding}
        >
          {AppStrings.CASINO_CONFIG_VIEWS_ADD_CANCEL}
        </button>
        <button
          type="button"
          className="views-add-submit"
          onClick={handleAdd}
          disabled={selected.size === 0 || adding}
        >
          {selected.size > 0
            ? formatString(AppStrings.CASINO_CONFIG_VIEWS_ADD_SUBMIT_WITH_COUNT, {
                count: String(selected.size),
              })
            : AppStrings.CASINO_CONFIG_VIEWS_ADD_SUBMIT}
        </button>
      </div>
    </AntModal>
  );
}

function ViewsConfigurationPage() {
  usePageTitle(AppStrings.CASINO_CONFIG_VIEWS_TITLE);
  const { setActiveSidebarTab } = useContext(AppContext);
  // Operators with the `live_casino` flag run a separate Live Casino page;
  // the page selector that switches between storefront pages only appears when
  // the flag is on. While the flag status is still loading (`isEnabled === null`)
  // we keep the control hidden and stay on the default casino page.
  const { isEnabled: liveCasinoEnabled } = usePamFeature("liveCasino");
  const { getName: getCollectionDisplayName } = useCurationDisplayName();
  // Storefront pages (Social Casino, Live casino, ...) are fetched dynamically
  // so new pages added server-side appear without a frontend release. Only
  // fetched when the flag is on — otherwise we stay on the default page.
  const {
    data: pagesData,
    isError: pagesError,
    isLoading: pagesLoading,
  } = useAdminPages(Boolean(liveCasinoEnabled));
  const pages = useMemo(() => pagesData ?? [], [pagesData]);
  // Empty until a page code is resolved: when the flag is off we fall back to
  // the default page, and when it is on we adopt the backend default once the
  // pages list loads. This defers the first views request until we know the
  // correct page, avoiding a wrong-page round-trip on mount.
  const [pageCode, setPageCode] = useState<string>("");
  const [activeView, setActiveView] = useState<ViewKey | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  // Default to `true` so admins see the full list on first load — hiding
  // hidden placements by default makes it look like rows have silently
  // disappeared whenever someone switches them off elsewhere.
  const [showHidden, setShowHidden] = useState(true);
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [removeTarget, setRemoveTarget] =
    useState<AdminViewPlacement | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS.CASINO, "CONFIGURATION");
  }, [setActiveSidebarTab]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  // Resolve the initial page code. When the live_casino flag is off we never
  // fetch pages, so fall back to the default page to preserve the single-casino
  // flow. (`isEnabled === null` means the flag is still loading — wait for it.)
  useEffect(() => {
    if (liveCasinoEnabled === false && pageCode === "") {
      setPageCode(DEFAULT_PAGE_CODE);
    }
  }, [liveCasinoEnabled, pageCode]);

  // Keep the selected page valid against the dynamic page list: snap to the
  // backend default (or the first page) whenever the current selection is not
  // one of the available pages (e.g. on first load or if a page is removed).
  useEffect(() => {
    if (pages.length === 0) return;
    if (pages.some((p) => p.code === pageCode)) return;
    const fallback = pages.find((p) => p.is_default) ?? pages[0];
    setPageCode(fallback.code);
  }, [pages, pageCode]);

  // Surface a visible error when the pages list fails to load instead of
  // silently hiding the selector for flag-enabled operators, and fall back to
  // the default page so the Social Casino views still render.
  useEffect(() => {
    if (!pagesError) return;
    message.error(AppStrings.CASINO_CONFIG_VIEWS_FETCH_PAGES_FALLBACK);
    setPageCode((current) => current || DEFAULT_PAGE_CODE);
  }, [pagesError]);

  // Switching between storefront pages scopes to a different set of
  // placements, so any in-flight search term or "show hidden" toggle from the
  // previous page should not carry over.
  useEffect(() => {
    setSearchQuery("");
    setDebouncedSearch("");
    setShowHidden(true);
  }, [pageCode]);

  const { data: views, isLoading: viewsLoading } = useAdminViews(pageCode);
  const viewKeys = views ?? [];

  // Select the first view on initial load, and re-select it if the active
  // view no longer exists in the current page's view list (e.g. after
  // switching between the Casino and Live Casino pages).
  useEffect(() => {
    if (!views || views.length === 0) return;
    if (!activeView || !views.includes(activeView)) {
      setActiveView(views[0]);
    }
  }, [views, activeView]);

  // Server-side search: pass the debounced term straight to the API so a
  // 7,000-row catalog stays fast and we don't filter a partial page.
  const {
    data: placementsData,
    isLoading: placementsLoading,
    isError,
    error,
  } = useAdminViewPlacements(activeView, debouncedSearch || undefined, pageCode);

  const placements: AdminViewPlacement[] = useMemo(
    () => placementsData?.items ?? [],
    [placementsData],
  );

  // The display query above is search-filtered, so we deliberately fire a
  // second, *un*-searched query whose only job is to feed
  // `existingPlacementCodes`. Without this, when the page search is
  // narrowing the visible list, the Add Collection picker would treat
  // already-placed-but-currently-hidden collections as addable and the
  // backend would reject the duplicate POST. React Query dedupes the
  // request when no search is active, so this costs nothing extra in the
  // common case.
  const { data: allPlacementsData, isLoading: allPlacementsLoading } =
    useAdminViewPlacements(activeView, undefined, pageCode);
  const existingPlacementCodes = useMemo(
    () =>
      new Set(
        (allPlacementsData?.items ?? []).map((p) => p.collection.code),
      ),
    [allPlacementsData],
  );

  // The `Show hidden` toggle stays a client-side filter — the API returns
  // both active and inactive placements and we let the user toggle visibility.
  const filteredPlacements = useMemo(
    () => (showHidden ? placements : placements.filter((p) => p.is_active)),
    [placements, showHidden],
  );

  // Each mutation handler guards on `activeView` before calling
  // `mutateAsync`, so passing an empty string here is safe even though the
  // empty value never produces a real request.
  const safeActiveView: ViewKey = activeView ?? "";
  const { mutateAsync: movePlacement } = useMoveAdminViewPlacement(
    safeActiveView,
    pageCode,
  );
  const { mutateAsync: patchPlacement } = usePatchAdminViewPlacement(
    safeActiveView,
    pageCode,
  );
  const { mutateAsync: deletePlacement, isPending: removing } =
    useDeleteAdminViewPlacement(safeActiveView, pageCode);

  const handleDragStart = (idx: number) => setDragIdx(idx);
  const handleDragOver = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    setDragOverIdx(idx);
  };
  const handleDragCancel = () => {
    setDragIdx(null);
    setDragOverIdx(null);
  };
  const handleDrop = async (dropIdx: number) => {
    const items = filteredPlacements;
    const moving =
      dragIdx !== null && dragIdx !== dropIdx ? items[dragIdx] : null;
    const target = items[dropIdx];
    setDragIdx(null);
    setDragOverIdx(null);
    if (!moving || !target || !activeView) return;
    try {
      // Send the target row's actual backend position rather than the
      // local list index. When a search term is active or `Show hidden`
      // is off, the filtered list is a subset of the full backend list,
      // so `dropIdx + 1` would map to the wrong global slot. For an
      // unpinned target, `display_order` already IS that backend slot.
      // For a pinned target, `display_order` is assigned in reverse
      // (the top pinned row holds the most negative value), the opposite
      // of the signed, band-relative `target_position` the move endpoint
      // expects (`-1` = top of the pinned band) — recompute it as the
      // target's 1-based rank within the pinned band instead.
      let targetPosition = target.display_order;
      if (target.is_pinned) {
        const pinnedIdx = (allPlacementsData?.items ?? [])
          .filter((p) => p.is_pinned)
          .findIndex((p) => p.collection.code === target.collection.code);
        targetPosition =
          pinnedIdx === -1 ? VIEW_PLACEMENT_PIN_TO_TOP : -(pinnedIdx + 1);
      }
      await movePlacement({
        collection_code: moving.collection.code,
        target_position: targetPosition,
      });
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message || AppStrings.CASINO_CONFIG_VIEWS_MOVE_FAILED,
        ),
      );
    }
  };

  const handleTogglePin = async (placement: AdminViewPlacement) => {
    if (!activeView) return;
    try {
      let targetPosition: number;
      if (placement.is_pinned) {
        const allItems = allPlacementsData?.items ?? [];
        const pinnedCount = allItems.filter((p) => p.is_pinned).length;
        targetPosition = Math.max(1, pinnedCount);
      } else {
        targetPosition = VIEW_PLACEMENT_PIN_TO_TOP;
      }
      await movePlacement({
        collection_code: placement.collection.code,
        target_position: targetPosition,
      });
      message.success(
        placement.is_pinned
          ? AppStrings.CASINO_CONFIG_VIEWS_UNPIN_SUCCESS
          : AppStrings.CASINO_CONFIG_VIEWS_PIN_SUCCESS,
      );
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message || AppStrings.CASINO_CONFIG_VIEWS_PIN_FAILED,
        ),
      );
    }
  };

  const handleToggleActive = async (placement: AdminViewPlacement) => {
    if (!activeView) return;
    const nextActive = !placement.is_active;
    try {
      await patchPlacement({
        collection_code: placement.collection.code,
        is_active: nextActive,
      });
      message.success(
        nextActive
          ? AppStrings.CASINO_CONFIG_VIEWS_TOGGLE_SUCCESS_SHOWN
          : AppStrings.CASINO_CONFIG_VIEWS_TOGGLE_SUCCESS_HIDDEN,
      );
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message ||
            AppStrings.CASINO_CONFIG_VIEWS_TOGGLE_FAILED,
        ),
      );
    }
  };

  const handleConfirmRemove = async () => {
    if (!removeTarget || !activeView) return;
    try {
      await deletePlacement(removeTarget.collection.code);
      message.success(AppStrings.CASINO_CONFIG_VIEWS_REMOVED_SUCCESS);
      setRemoveTarget(null);
    } catch (err: unknown) {
      message.error(
        getApiErrorMessage(
          err,
          (err as Error)?.message ||
            AppStrings.CASINO_CONFIG_VIEWS_REMOVE_FAILED,
        ),
      );
    }
  };

  // Until a page code is resolved we cannot fetch views (the query is disabled
  // on an empty page code), so treat that window as loading to avoid flashing
  // the empty state. This also covers the flag still resolving
  // (`liveCasinoEnabled === null`), where no page code is selected yet. The
  // flag-off flow snaps to the default page synchronously, so it is excluded.
  const pageCodeResolving = liveCasinoEnabled !== false && !pageCode;
  const isLoading =
    pageCodeResolving ||
    (Boolean(liveCasinoEnabled) && pagesLoading) ||
    viewsLoading ||
    (Boolean(activeView) && (placementsLoading || allPlacementsLoading));

  return (
    <>
      <div className="views-shell">
        <div className="views-config">
          <div className="views-header">
            <div className="views-header-main">
              <Tooltip
                placement="bottomLeft"
                classNames={{ root: "views-title-tooltip" }}
                title={
                  <span>
                    {AppStrings.CASINO_CONFIG_VIEWS_INFO_LEAD}
                    <b>{AppStrings.CASINO_CONFIG_VIEWS_INFO_TERM_SLOT}</b>
                    {AppStrings.CASINO_CONFIG_VIEWS_INFO_AFTER_SLOT}
                    <b>
                      {AppStrings.CASINO_CONFIG_VIEWS_INFO_TERM_SHORTCUTS}
                    </b>
                    {AppStrings.CASINO_CONFIG_VIEWS_INFO_AFTER_SHORTCUTS}
                    <b>
                      {AppStrings.CASINO_CONFIG_VIEWS_INFO_TERM_CAROUSELS}
                    </b>
                    {AppStrings.CASINO_CONFIG_VIEWS_INFO_AFTER_CAROUSELS}
                  </span>
                }
              >
                <span
                  className="views-title-info"
                  aria-label={AppStrings.CASINO_CONFIG_VIEWS_INFO_ARIA}
                >
                  <InfoCircleOutlined />
                </span>
              </Tooltip>
            {liveCasinoEnabled && pages.length > 1 && (
              <ViewsPageSelector
                pages={pages}
                value={pageCode}
                onChange={setPageCode}
                ariaLabel={
                  AppStrings.CASINO_CONFIG_VIEWS_PAGE_SELECTOR_LABEL
                }
              />
            )}
            </div>
            <p className="views-subtitle">
              {AppStrings.CASINO_CONFIG_VIEWS_SUBTITLE}
            </p>
          </div>

          {viewKeys.length > 0 && (
            <div className="views-tabs" role="tablist">
              {viewKeys.map((view) => {
                const isActiveTab = view === activeView;
                const tooltipText = VIEW_TAB_INFO[view];
                return (
                  <button
                    key={view}
                    type="button"
                    role="tab"
                    aria-selected={isActiveTab}
                    className={`views-tab ${
                      isActiveTab ? "views-tab--active" : ""
                    }`}
                    onClick={() => setActiveView(view)}
                  >
                    <span className="views-tab-label">
                      {labelForView(view)}
                    </span>
                    {tooltipText && (
                      <Tooltip title={tooltipText} placement="bottom">
                        <span
                          className="views-tab-info"
                          aria-label={tooltipText}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <InfoCircleOutlined />
                        </span>
                      </Tooltip>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          <div className="views-controls">
            <Input
              className="views-search"
              prefix={<SearchOutlined />}
              placeholder={
                AppStrings.CASINO_CONFIG_VIEWS_SEARCH_PLACEHOLDER
              }
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              allowClear
            />
            <label className="views-show-hidden">
              <span className="views-show-hidden-label">
                {AppStrings.CASINO_CONFIG_VIEWS_SHOW_HIDDEN}
              </span>
              <input
                type="checkbox"
                checked={showHidden}
                onChange={(e) => setShowHidden(e.target.checked)}
                aria-label={AppStrings.CASINO_CONFIG_VIEWS_SHOW_HIDDEN}
              />
              <span className="views-show-hidden-slider" />
            </label>
            <Button
              className="views-add-btn"
              icon={<PlusOutlined />}
              type="primary"
              disabled={!activeView}
              onClick={() => setAddOpen(true)}
            >
              {AppStrings.CASINO_CONFIG_VIEWS_ADD_COLLECTION}
            </Button>
          </div>

          <div className="views-list">
            {isLoading ? (
              <div className="views-loader">
                <RebetLoader height="88px" width="88px" />
              </div>
            ) : isError ? (
              <div className="views-empty">
                <Empty
                  description={getApiErrorMessage(
                    error,
                    (error as Error)?.message ||
                      AppStrings.CASINO_CONFIG_VIEWS_LOAD_ERROR,
                  )}
                />
              </div>
            ) : filteredPlacements.length === 0 ? (
              <div className="views-empty">
                <Empty
                  description={
                    debouncedSearch
                      ? AppStrings.CASINO_CONFIG_VIEWS_EMPTY_FILTERED
                      : AppStrings.CASINO_CONFIG_VIEWS_EMPTY
                  }
                />
              </div>
            ) : (
              filteredPlacements.map((placement, idx) => (
                <PlacementRow
                  key={`${placement.collection.code}-${placement.display_order}`}
                  placement={placement}
                  index={idx}
                  dragOverIdx={dragOverIdx}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragCancel={handleDragCancel}
                  onTogglePin={handleTogglePin}
                  onToggleActive={handleToggleActive}
                  onRequestRemove={setRemoveTarget}
                  getName={getCollectionDisplayName}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <AntModal
        open={removeTarget !== null}
        onCancel={() => setRemoveTarget(null)}
        footer={null}
        width={480}
        className="views-remove-modal"
        closeIcon={<CloseOutlined />}
        destroyOnHidden
      >
        <h3 className="views-remove-title">
          {AppStrings.CASINO_CONFIG_VIEWS_REMOVE_TITLE_PREFIX}{" "}
          &lsquo;
          {removeTarget ? (
            <CurationEntityName
              entity={removeTarget.collection}
              displayName={getCollectionDisplayName(removeTarget.collection)}
            />
          ) : null}
          &rsquo;{" "}
          {AppStrings.CASINO_CONFIG_VIEWS_REMOVE_TITLE_SUFFIX}
        </h3>
        <div className="views-remove-actions">
          <button
            type="button"
            className="views-remove-cancel"
            onClick={() => setRemoveTarget(null)}
            disabled={removing}
          >
            {AppStrings.CASINO_CONFIG_VIEWS_REMOVE_CANCEL}
          </button>
          <button
            type="button"
            className="views-remove-confirm"
            onClick={handleConfirmRemove}
            disabled={removing}
          >
            {AppStrings.CASINO_CONFIG_VIEWS_REMOVE_CONFIRM}
          </button>
        </div>
      </AntModal>

      <AddViewPlacementModal
        open={addOpen}
        view={activeView}
        pageCode={pageCode}
        existingCodes={existingPlacementCodes}
        onClose={() => setAddOpen(false)}
        getName={getCollectionDisplayName}
      />
    </>
  );
}

export default ViewsConfigurationPage;
